import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import RecipientBrowser from './RecipientBrowser';
import { showToast } from '../utils/alerts-messages';
import {
    fetchUsers,
    fetchStagedRecipients,
    createUser,
    deleteUser,
    updateUser,
    fetchTags,
    fetchUserTags,
    createTag,
    deleteTag,
    setUserTags
} from '../utils/consoleApi';
import { listDeploymentUsersFromCloud } from '../utils/cloudApi';
import { formatUsersCsv } from '../utils/csvParser';
import CsvImportModal from './CsvImportModal';
import TagBadge from './TagBadge';
import TagManagementModal from './TagManagementModal';
import UserTagFilter from './UserTagFilter';
import UserTagAssignment from './UserTagAssignment';
import AddRecipientModal from './AddRecipientModal';
import { listPopulationTags, createPopulationTag, deletePopulationTag, createRecipient } from '../utils/populationApi';
import PanelHeaderActions from './PanelHeaderActions';
import { SURVEY_RECIPIENTS_ACTIONS } from './panelActions';
import useContextMenu from '../hooks/useContextMenu';
import ContextMenu from './ContextMenu';
import ConfirmModal from './ConfirmModal';

/**
 * UsersPanel
 * 
 * Responsibility: Complete user management interface
 * Encapsulates: user CRUD, CSV export, deployment linking
 * 
 * @param {string} selectedDeploymentId - Currently selected deployment
 * @param {string} surveyName - Name of current survey
 * @param {Function} openPresentor - Callback to open presentor link
 * @param {Function} onSelectionChange - Callback (selectedUserIds, selectionData) => void
 *   Called when user selection changes, provides Set of user IDs and array of selected user data
 */
const UsersPanel = forwardRef(function UsersPanel({
    selectedDeploymentId,
    surveyName,
    openPresentor,
    onSelectionChange,
    onStaged,
    orgId,
    staging,
}, ref) {
    // User management state
    const [users, setUsers] = useState([]);
    const [usersSearch, setUsersSearch] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [deploymentUsers, setDeploymentUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmModal, setConfirmModal] = useState(null);
    const [showCsvImport, setShowCsvImport] = useState(false);
    const [showRecipientBrowser, setShowRecipientBrowser] = useState(false);
    const [recipientBrowserSelection, setRecipientBrowserSelection] = useState(null);

    // Filter & Sort state
    const [sortBy, setSortBy] = useState('email');
    const [sortOrder, setSortOrder] = useState('ASC');

    // Tag management state
    const [tags, setTags] = useState([]);
    const [selectedTagFilter, setSelectedTagFilter] = useState(new Set());
    const [showTagModal, setShowTagModal] = useState(false);
    const [userTagsMap, setUserTagsMap] = useState({});
    const [editingUserId, setEditingUserId] = useState(null);
    const [showTagAssignment, setShowTagAssignment] = useState(false);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
    const [editingCell, setEditingCell] = useState(null); // { userId, field, value }
    const [selectFilteredSearch, setSelectFilteredSearch] = useState('');
    const [showSelectByTagDropdown, setShowSelectByTagDropdown] = useState(false);
    const selectAllRef = React.useRef(null);
    const { openMenu, contextMenuProps } = useContextMenu();

    // Update indeterminate state of select all checkbox
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = selectedUserIds.size > 0 && selectedUserIds.size < users.length;
        }
    }, [selectedUserIds, users]);

    // Load staged recipients (the panel's data source)
    const loadUsers = useCallback(async () => {
        try {
            const result = await fetchStagedRecipients(usersSearch, 1000, sortBy, sortOrder, orgId);
            setUsers(result.items || []);
            setError('');
        } catch (err) {
            setError(typeof err === 'string' ? err : (err?.message || String(err) || 'Failed to load users'));
        }
    }, [usersSearch, sortBy, sortOrder, orgId]);

    const handleHeaderClick = (column) => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(column);
            setSortOrder('ASC'); // Default new column to ASC
        }
    };

    // Load deployment users (for link column)
    const loadDeploymentUsers = useCallback(async () => {
        if (!selectedDeploymentId) {
            setDeploymentUsers([]);
            return;
        }
        try {
            const result = await listDeploymentUsersFromCloud(selectedDeploymentId);
            setDeploymentUsers(result.items || []);
        } catch (err) {
            console.error('Failed to load deployment users:', err);
        }
    }, [selectedDeploymentId]);

    // Initial load
    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Load deployment users when deployment changes
    useEffect(() => {
        loadDeploymentUsers();
    }, [loadDeploymentUsers]);

    // Notify parent when selection changes (for toolbar operations)
    useEffect(() => {
        if (onSelectionChange) {
            // Calculate deployment user lookup
            const deploymentUserLookup = Object.fromEntries(
                (deploymentUsers || []).map(du => [du.user_id, du])
            );
            // Build selection data with user info and deployment link
            const selectionData = Array.from(selectedUserIds).map(userId => {
                const user = users.find(u => u.user_id === userId) || {};
                const du = deploymentUserLookup[userId];
                return {
                    user_id: userId,
                    email: user.email || '',
                    name: user.name || '',
                    phone_number: user.phone_number || '',
                    preferred_channel: user.preferred_channel || 'browser',
                    link_url: (du && du.link_url) || ''
                };
            });
            onSelectionChange(selectedUserIds, selectionData);
        }
    }, [selectedUserIds, users, deploymentUsers, onSelectionChange]);

    // Load tags on mount
    useEffect(() => {
        const loadTags = async () => {
            try {
                const result = await fetchTags();
                setTags(result.items || []);
            } catch (err) {
                console.error('Failed to load tags:', err);
            }
        };
        loadTags();
    }, []);

    // Load user tags when users change
    useEffect(() => {
        const loadAllUserTags = async () => {
            if (users.length === 0) {
                setUserTagsMap({});
                return;
            }
            const tagMap = {};
            for (const user of users) {
                try {
                    const result = await fetchUserTags(user.user_id);
                    tagMap[user.user_id] = result.items || [];
                } catch (err) {
                    console.error(`Failed to load tags for user ${user.user_id}:`, err);
                    tagMap[user.user_id] = [];
                }
            }
            setUserTagsMap(tagMap);
        };
        loadAllUserTags();
    }, [users]);

    // Create user — opens modal instead of window.prompt()
    const handleCreateUser = () => {
        setShowAddRecipientModal(true);
    };

    const handleAddRecipientSave = async ({ email, name, phone_number }) => {
        setLoading(true);
        try {
            await createUser(email, name, false, phone_number);
            await loadUsers();
        } catch (err) {
            throw err; // Let the modal display the error
        } finally {
            setLoading(false);
        }
    };

    // Delete selected users
    const handleDeleteUsers = async () => {
        if (selectedUserIds.size === 0) return;
        setConfirmModal({
            title: 'Delete users?',
            message: `Permanently delete ${selectedUserIds.size} user(s)?`,
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    setLoading(true);
                    for (const userId of Array.from(selectedUserIds)) {
                        await deleteUser(userId);
                    }
                    setSelectedUserIds(new Set());
                    await loadUsers();
                } catch (err) {
                    setError(typeof err === 'string' ? err : (err?.message || String(err) || 'Failed to delete users'));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Return selected recipients to the Browser (set staged = false)
    const handleUnstageSelected = async () => {
        if (selectedUserIds.size === 0) return;
        setLoading(true);
        setError('');
        let moved = 0;
        try {
            for (const userId of selectedUserIds) {
                try {
                    await updateUser(userId, { staged: false });
                    moved++;
                } catch (err) {
                    console.warn(`Failed to unstage ${userId}:`, err);
                }
            }
            setSelectedUserIds(new Set());
            await loadUsers();
            onStaged?.();
        } catch (err) {
            setError(`Return to Browser failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Inline editing handlers
    const handleStartEdit = (userId, field, value) => {
        setEditingCell({ userId, field, value });
    };

    const handleCancelEdit = () => {
        setEditingCell(null);
    };

    const handleSaveEdit = async (userId, field, newValue) => {
        try {
            setLoading(true);
            await updateUser(userId, { [field]: newValue });
            await loadUsers();
            setEditingCell(null);
        } catch (err) {
            setError(typeof err === 'string' ? err : (err?.message || String(err) || 'Failed to update user'));
        } finally {
            setLoading(false);
        }
    };

    // Panel always shows staged recipients (those with staged = true),
    // regardless of which deployment is selected
    const baseUsers = users;

    // Compute filtered users (same logic as render)
    const filteredUsers = baseUsers.filter(user => {
        if (selectedTagFilter.size === 0) return true;
        const uTags = userTagsMap[user.user_id] || [];
        const uTagIds = new Set(uTags.map(t => t.tag_id));
        for (const tagId of selectedTagFilter) {
            if (!uTagIds.has(tagId)) return false;
        }
        return true;
    });

    const handleToggleSelectAll = () => {
        if (selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0) {
            setSelectedUserIds(new Set());
        } else {
            setSelectedUserIds(new Set(filteredUsers.map(u => u.user_id)));
        }
    };

    // Select Filtered: check all recipients whose name or email matches a search string
    const handleSelectFiltered = () => {
        const q = selectFilteredSearch.trim().toLowerCase();
        if (!q) return;
        const matching = filteredUsers.filter(u =>
            (u.email || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q)
        );
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            matching.forEach(u => next.add(u.user_id));
            return next;
        });
        setSelectFilteredSearch('');
    };

    // Select by Tag: check all recipients that have a specific tag
    const handleSelectByTag = (tagId) => {
        const matching = users.filter(u => {
            const uTags = userTagsMap[u.user_id] || [];
            return uTags.some(t => t.tag_id === tagId);
        });
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            matching.forEach(u => next.add(u.user_id));
            return next;
        });
        setShowSelectByTagDropdown(false);
    };

    // CSV Export
    // BUG-111: pass the full user object to formatUsersCsv rather than
    // narrowing here. The CSV's column set is owned by USER_CSV_COLUMNS in
    // csvParser.js — narrowing here was dropping fields (preferred_language,
    // organization, recipient_group, opted_out, channel_identities, the
    // round-trip IDs) before they ever reached the formatter. Tags are the
    // only field we still have to merge in, since they're stored in a
    // separate per-user map rather than on the recipient row.
    const handleCsvExport = () => {
        const usersData = users.map(user => ({
            ...user,
            tags: (userTagsMap[user.user_id] || []).map(tag => tag.tag_name),
        }));

        const csv = formatUsersCsv(usersData);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // CSV Import
    const handleCsvImport = async (parsedUsers) => {
        if (!parsedUsers || parsedUsers.length === 0) return;

        // A rough count, used only to decide whether to ask the author what to
        // do about duplicates. It looks at the recipients loaded into this
        // panel, so it is a floor rather than a total. sam-dbam makes the real
        // decision, per row, against the whole account.
        const existingEmails = new Set(users.map(u => u.email.toLowerCase()));
        const duplicates = parsedUsers.filter(u => existingEmails.has(u.email.toLowerCase()));

        const proceedWithImport = async (shouldUpsert) => {
            setLoading(true);
            let successCount = 0;
            let skipCount = 0;
            let errorCount = 0;
            const unknownTagNames = new Set();

            for (const user of parsedUsers) {
                try {
                    // Send every field the CSV carried. This used to call
                    // createUser(email, name), which dropped the phone number,
                    // channel, language, locale, timezone, group, and opt-out
                    // state without saying so.
                    //
                    // sam-dbam decides whether this is a duplicate, on email
                    // plus name, across the whole account. The check used to be
                    // made here against the page on screen, which missed every
                    // duplicate on any other page.
                    const { tags: _tags, ...fields } = user;
                    const created = await createRecipient({ ...fields, upsert: shouldUpsert });

                    if (created?.skipped) {
                        skipCount++;
                        continue;
                    }

                    // Assign tags if present. Tag names that match nothing are
                    // collected and reported at the end — an import never
                    // creates a tag.
                    if (user.tags && user.tags.length > 0) {
                        const tagIds = [];
                        for (const tagName of user.tags) {
                            const tag = tags.find(t => t.tag_name.toLowerCase() === tagName.toLowerCase());
                            if (tag) tagIds.push(tag.tag_id);
                            else unknownTagNames.add(tagName);
                        }

                        // sam-dbam returns the recipient under its own name for
                        // the id. The Users Panel calls the same value user_id.
                        const recipientId = created?.global_recipient_id || created?.user_id;
                        if (tagIds.length > 0 && recipientId) {
                            await setUserTags(recipientId, tagIds);
                        }
                    }

                    successCount++;
                } catch (err) {
                    errorCount++;
                    console.error(`Failed to import ${user.email}:`, err);
                }
            }

            setLoading(false);
            loadUsers(); // Refresh list

            let summary = `Import complete: ${successCount} imported/updated. ${skipCount} skipped. ${errorCount} errors.`;
            if (unknownTagNames.size > 0) {
                summary += `\n\nThese tag names do not exist and were not applied: ${[...unknownTagNames].join(', ')}. Create them in Tag Management, then import again.`;
            }
            showToast(summary, errorCount > 0 || unknownTagNames.size > 0 ? 'info' : 'success');
        };

        if (duplicates.length > 0) {
            setConfirmModal({
                title: 'Overwrite existing users?',
                message: `The import contains ${duplicates.length} existing user(s). Would you like to OVERWRITE them with the data from the CSV, or skip duplicates and only import new users?`,
                confirmLabel: 'Overwrite',
                cancelLabel: 'Skip Duplicates',
                onConfirm: async () => {
                    setConfirmModal(null);
                    await proceedWithImport(true);
                },
                onCancel: async () => {
                    setConfirmModal(null);
                    await proceedWithImport(false);
                }
            });
        } else {
            await proceedWithImport(false);
        }
    };

    // Deployment user lookup for link column
    const deploymentUserLookup = Object.fromEntries(
        (deploymentUsers || []).map(du => [du.user_id, du])
    );

    // Expose handlers to parent via ref
    useImperativeHandle(ref, () => ({
        createUser: handleCreateUser,
        deleteUsers: handleDeleteUsers,
        csvExport: handleCsvExport,
        csvImport: () => setShowCsvImport(true),
        manageTags: () => setShowTagModal(true),
        assignTags: () => setShowBulkTagModal(true),
        browsePopulation: (prePopulateSelection) => {
            setRecipientBrowserSelection(Array.isArray(prePopulateSelection) ? prePopulateSelection : null);
            setShowRecipientBrowser(true);
        },
        unstageUsers: handleUnstageSelected,
        loadDeploymentUsers,
        loadUsers,
        getDeploymentUsers: () => deploymentUsers,
        clearSelection: () => setSelectedUserIds(new Set()),
        editUser: () => {
            // Edit the first selected user inline
            const userId = Array.from(selectedUserIds)[0];
            if (!userId) return;
            const user = baseUsers.find(u => u.user_id === userId);
            if (user) handleStartEdit(userId, 'name', user.name || '');
        },
    }));

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div id="console-recipients-panel" />
            <PanelHeaderActions
                actions={SURVEY_RECIPIENTS_ACTIONS}
                selection={selectedUserIds.size > 0}
                state={{}}
            />
            {selectedDeploymentId ? (
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div><strong>Survey:</strong> {surveyName}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                            <strong>Deployment:</strong> {selectedDeploymentId}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ marginBottom: 8, color: '#5F6368' }}>
                    Select a deployed survey to manage recipients
                </div>
            )}

            {error && (
                <div style={{ backgroundColor: '#FFEBEE', color: '#C62828', padding: 8, borderRadius: 4, marginBottom: 8, fontSize: 13 }}>
                    {error}
                </div>
            )}


            {/* Controls removed - use Users menu commands */}

            {/* Tag Filter */}
            {tags.length > 0 && (
                <UserTagFilter
                    availableTags={tags}
                    selectedTagIds={selectedTagFilter}
                    onTagSelectionChange={setSelectedTagFilter}
                />
            )}

            {/* Selection Tools */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                {/* Select Filtered */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder="Name or email…"
                        value={selectFilteredSearch}
                        onChange={e => setSelectFilteredSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSelectFiltered(); }}
                        style={{ padding: '3px 8px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, width: 150 }}
                    />
                    <button
                        onClick={handleSelectFiltered}
                        disabled={!selectFilteredSearch.trim()}
                        style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: selectFilteredSearch.trim() ? 'pointer' : 'default', color: selectFilteredSearch.trim() ? '#202124' : '#BDC1C6' }}
                    >Select Matching</button>
                </div>
                {/* Select by Tag */}
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowSelectByTagDropdown(!showSelectByTagDropdown)}
                        style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: 'pointer' }}
                    >Select by Tag ▾</button>
                    {showSelectByTagDropdown && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2, background: '#fff', border: '1px solid #DADCE0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: 160, padding: '4px 0' }}>
                            {tags.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#999' }}>No tags</div>}
                            {tags.map(t => (
                                <button
                                    key={t.tag_id}
                                    onClick={() => handleSelectByTag(t.tag_id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}
                                    onMouseEnter={e => e.target.style.background = 'var(--color-bg-base)'}
                                    onMouseLeave={e => e.target.style.background = 'transparent'}
                                >
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.tag_color || '#BDC1C6', flexShrink: 0 }} />
                                    {t.tag_name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Clear selection */}
                {selectedUserIds.size > 0 && (
                    <button
                        onClick={() => setSelectedUserIds(new Set())}
                        style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: 'pointer', color: '#5F6368' }}
                    >Clear ({selectedUserIds.size})</button>
                )}
            </div>

            {/* Users Table */}
            <div style={{ flex: 1, overflow: 'auto', paddingBottom: 40 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: '8px 6px', textAlign: 'left', width: 40 }}>
                                <input
                                    type="checkbox"
                                    ref={selectAllRef}
                                    checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length}
                                    onChange={handleToggleSelectAll}
                                />
                            </th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleHeaderClick('email')}>
                                Email {sortBy === 'email' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleHeaderClick('name')}>
                                Name {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>
                            <th style={{ padding: '8px 6px', textAlign: 'left' }}>Tags</th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', width: 60 }}>Link</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => {
                            const checked = selectedUserIds.has(user.user_id);
                            const du = deploymentUserLookup[user.user_id];
                            const linkUrl = du && du.link_url ? du.link_url : '';
                            const userTags = userTagsMap[user.user_id] || [];

                            return (
                                <tr key={user.user_id} onContextMenu={(e) => {
                                    const items = SURVEY_RECIPIENTS_ACTIONS
                                        .filter(a => !a.hideWhen || !a.hideWhen({}))
                                        .map(a => ({ label: a.label, onClick: a.onClick, dimmed: a.requiresSelection && selectedUserIds.size === 0 }));
                                    openMenu(e, items);
                                }} style={{ borderBottom: '1px solid #E8EAED' }}>
                                    <td style={{ padding: '4px 6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                                setSelectedUserIds(prev => {
                                                    const next = new Set(prev);
                                                    if (e.target.checked) next.add(user.user_id);
                                                    else next.delete(user.user_id);
                                                    return next;
                                                });
                                            }}
                                        />
                                    </td>
                                    <td
                                        style={{ padding: '4px 6px' }}
                                        onDoubleClick={() => handleStartEdit(user.user_id, 'email', user.email)}
                                    >
                                        {editingCell?.userId === user.user_id && editingCell?.field === 'email' ? (
                                            <input
                                                autoFocus
                                                style={{ width: '100%' }}
                                                value={editingCell.value}
                                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                                onBlur={() => handleSaveEdit(user.user_id, 'email', editingCell.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit(user.user_id, 'email', editingCell.value);
                                                    if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                            />
                                        ) : (
                                            <strong>{user.email}</strong>
                                        )}
                                    </td>
                                    <td
                                        style={{ padding: '4px 6px' }}
                                        onDoubleClick={() => handleStartEdit(user.user_id, 'name', user.name || '')}
                                    >
                                        {editingCell?.userId === user.user_id && editingCell?.field === 'name' ? (
                                            <input
                                                autoFocus
                                                style={{ width: '100%' }}
                                                value={editingCell.value}
                                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                                onBlur={() => handleSaveEdit(user.user_id, 'name', editingCell.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit(user.user_id, 'name', editingCell.value);
                                                    if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                            />
                                        ) : (
                                            user.name || ''
                                        )}
                                    </td>
                                    <td
                                        style={{
                                            padding: '4px 6px',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 4,
                                            cursor: 'pointer',
                                            minHeight: 32,
                                            backgroundColor: 'rgba(91, 159, 255, 0.03)',
                                            borderRadius: 4
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingUserId(user.user_id);
                                            setShowTagAssignment(true);
                                        }}
                                        title="Click to manage tags for this user"
                                    >
                                        {userTags.length > 0 ? (
                                            userTags.map(tag => (
                                                <TagBadge key={tag.tag_id} tag={tag} size="sm" />
                                            ))
                                        ) : (
                                            <span style={{ color: '#ccc', fontSize: 11, alignSelf: 'center' }}>(no tags)</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '4px 6px', fontSize: 12 }}>
                                        {linkUrl ? (
                                            <a
                                                href={linkUrl}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openPresentor?.(linkUrl);
                                                }}
                                                title="Open Presentor (auto-closes on completion)"
                                                rel="noreferrer"
                                            >
                                                link
                                            </a>
                                        ) : (
                                            <span style={{ color: '#999' }}>(no link)</span>
                                        )}
                                    </td>


                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Transfer footer bar ── Matches RecipientBrowser footer */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px',
                background: '#f8fafc', borderTop: '2px solid #5B9FFF',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    {selectedUserIds.size > 0 ? (
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                            {selectedUserIds.size} recipient{selectedUserIds.size !== 1 ? 's' : ''} selected
                        </span>
                    ) : (
                        <span style={{ fontSize: 13, color: '#64748b' }}>
                            Select recipients with checkboxes to move them to the Browser
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {selectedUserIds.size > 0 && (
                        <>
                            <button
                                onClick={() => setSelectedUserIds(new Set())}
                                style={{
                                    padding: '6px 14px', borderRadius: 6, border: '1px solid #DADCE0',
                                    fontSize: 13, cursor: 'pointer', background: '#fff',
                                }}
                            >Clear</button>
                            <button
                                onClick={handleUnstageSelected}
                                disabled={loading}
                                style={{
                                    padding: '6px 18px', borderRadius: 6, border: 'none',
                                    background: '#5B9FFF', color: '#fff', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', boxShadow: '0 1px 3px rgba(37,99,235,0.3)',
                                }}
                            >{loading ? 'Moving…' : `Move ${selectedUserIds.size} → Browser`}</button>
                        </>
                    )}
                </div>
            </div>

            {/* CSV Import Modal */}
            <CsvImportModal
                isOpen={showCsvImport}
                onClose={() => setShowCsvImport(false)}
                onImport={handleCsvImport}
            />

            {/* Add Recipient Modal */}
            <AddRecipientModal
                isOpen={showAddRecipientModal}
                onClose={() => setShowAddRecipientModal(false)}
                onSave={handleAddRecipientSave}
            />

            {/* Tag Management Modal */}
            <TagManagementModal
                isOpen={showTagModal}
                onClose={() => setShowTagModal(false)}
                onTagsChanged={async () => {
                    // Reload tags
                    try {
                        const result = await fetchTags();
                        setTags(result.items || []);
                    } catch (err) {
                        console.error('Failed to reload tags:', err);
                    }
                    // Reload user tags
                    if (users.length > 0) {
                        const tagMap = {};
                        for (const user of users) {
                            try {
                                const result = await fetchUserTags(user.user_id);
                                tagMap[user.user_id] = result.items || [];
                            } catch (err) {
                                tagMap[user.user_id] = [];
                            }
                        }
                        setUserTagsMap(tagMap);
                    }
                }}
                datastore={{
                    listTags: async () => {
                        const t = await listPopulationTags();
                        return { items: t };
                    },
                    createTag: (name, color) => createPopulationTag(name, color),
                    deleteTag: (id) => deletePopulationTag(id),
                }}
            />

            {/* Individual Tag Assignment Modal Overlay */}
            {showTagAssignment && (
                <div style={modalStyles.overlay} onClick={() => setShowTagAssignment(false)}>
                    <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
                        <div style={modalStyles.header}>
                            <h3 style={{ margin: 0 }}>Manage Tags for {users.find(u => u.user_id === editingUserId)?.email}</h3>
                            <button onClick={() => setShowTagAssignment(false)} style={modalStyles.closeButton}>×</button>
                        </div>
                        <div style={{ paddingTop: 16 }}>
                            <UserTagAssignment
                                userId={editingUserId}
                                userTags={userTagsMap[editingUserId] || []}
                                availableTags={tags}
                                onSave={async (userId, tagIds) => {
                                    try {
                                        setLoading(true);
                                        await setUserTags(userId, tagIds);
                                        // Refresh tags for this user
                                        const result = await fetchUserTags(userId);
                                        setUserTagsMap(prev => ({ ...prev, [userId]: result.items || [] }));
                                        setShowTagAssignment(false);
                                    } catch (err) {
                                        setError(typeof err === 'string' ? err : (err?.message || String(err) || 'Failed to save tags'));
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                                onCancel={() => setShowTagAssignment(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Tag Assignment Modal Overlay */}
            {showBulkTagModal && (
                <div style={modalStyles.overlay} onClick={() => setShowBulkTagModal(false)}>
                    <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
                        <div style={modalStyles.header}>
                            <h3 style={{ margin: 0 }}>Assign Tags to {selectedUserIds.size} selected users</h3>
                            <button onClick={() => setShowBulkTagModal(false)} style={modalStyles.closeButton}>×</button>
                        </div>
                        <div style={{ paddingTop: 16 }}>
                            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                                Select tags to add to all {selectedUserIds.size} selected users.
                                These tags will be <strong>merged</strong> with any existing tags they already have.
                            </p>
                            <UserTagAssignment
                                userId="bulk"
                                userTags={[]} // Start with no selection for bulk
                                availableTags={tags}
                                onSave={async (_, tagIds) => {
                                    try {
                                        setLoading(true);
                                        const userIds = Array.from(selectedUserIds);
                                        for (const userId of userIds) {
                                            const currentTags = userTagsMap[userId] || [];
                                            const currentIds = currentTags.map(t => t.tag_id);
                                            // Merge current with new unique selections
                                            const mergedIds = Array.from(new Set([...currentIds, ...tagIds]));
                                            await setUserTags(userId, mergedIds);
                                        }
                                        // Refresh user list and tags
                                        await loadUsers();
                                        setShowBulkTagModal(false);
                                        setSelectedUserIds(new Set());
                                    } catch (err) {
                                        setError(typeof err === 'string' ? err : (err?.message || String(err) || 'Failed to assign tags in bulk'));
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                                onCancel={() => setShowBulkTagModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
            <RecipientBrowser
                isOpen={showRecipientBrowser}
                onClose={() => { setShowRecipientBrowser(false); loadUsers(); }}
                selectedDeploymentId={selectedDeploymentId}
                onStaged={() => { loadDeploymentUsers(); loadUsers(); onStaged?.(); }}
                orgId={orgId}
                prePopulateSelection={recipientBrowserSelection}
                stagedRecipientIds={staging?.stagedIds}
                onAddToStaging={(ids, metaList) => staging?.add(ids, metaList)}
            />
            <ConfirmModal
                open={!!confirmModal}
                title={confirmModal?.title}
                message={confirmModal?.message}
                confirmLabel={confirmModal?.confirmLabel}
                cancelLabel={confirmModal?.cancelLabel}
                danger={confirmModal?.danger}
                onConfirm={confirmModal?.onConfirm}
                onCancel={confirmModal?.onCancel || (() => setConfirmModal(null))}
            />
            {contextMenuProps && <ContextMenu {...contextMenuProps} />}
        </div>
    );
});

export default UsersPanel;

const modalStyles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 24,
        width: '100%',
        maxWidth: 500,
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #E8EAED',
        paddingBottom: 12,
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: 28,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
        color: '#5F6368',
    }
};
